import {
  CUSTOMER_PRICING_RULES_MULTI_SPACE_PARAMETER_SUPPORT,
  CUSTOMER_PRICING_RULES_SPACE_PARAMETER_SUPPORT,
} from '@src/components/AppLayout/Panels/Dealer/DealerSpaceParameters';
import { useSpaceParameterControlledFilter } from '@src/components/AppLayout/SpaceParameters';
import { useFeatureFlag, useModeFilter, usePricingAggregationsFilter, useRoleAuth } from '@src/hooks';
import { useHedgeTradeSubAccountFilters } from '@src/hooks/filters/useHedgeTradeSubAccountFilters';
import { useCustomers, useCustomersByName } from '@src/hooks/useCustomer';
import { useCustomerSecurities, usePricingRules, useSubAccounts } from '@src/providers';
import type { PricingRule } from '@talos/kyoko';
import {
  ACTION,
  AggregationType,
  ButtonVariants,
  EMPTY_ARRAY,
  EntityAdmin,
  IconName,
  useAggregationsContext,
  useCurrenciesContext,
  useDisclosure,
  useFeeCurrenciesFilter,
  useMarketFeeModesFilter,
  useSecuritiesContext,
  useSymbolsFilter,
} from '@talos/kyoko';
import { isNil } from 'lodash-es';
import { useCallback, useMemo } from 'react';
import { DEFAULT_CUSTOMER_CREDITS_CURRENCIES } from '../CustomerCredits/utils';
import { DEALER_FEE_MODE_OPTIONS } from '../utils';
import { EditDefaultPricingRuleDialog } from './EditDefaultPricingRuleDialog';
import { useEditPricingRule } from './useEditPricingRule';
import { useRequiredCustomerFilter, useRequiredCustomerFilterProperty } from './useRequiredCustomerFilter';
import {
  CUSTOMER_PRICING_GROUP_COLUMN_DEF,
  CUSTOMER_PRICING_TABLE_COLUMNS,
  getCustomerPricingEntityForPatchPost,
  getCustomerPricingTableDrawerOptions,
  getPricingRuleEntityDisplayName,
  mapCustomerPricingServerDataToRecord,
} from './utils';

const CUSTOMER_PRICING_PERSIST_KEY = 'dealer/customer-pricing';

export function CustomerPricing() {
  const { isAuthorized } = useRoleAuth();
  const {
    pricingRuleAllowedCrossMarkets,
    pricingRuleAllowedCrossCurrencies,
    requireCustomerPricingRulesCounterpartyFilter,
  } = useFeatureFlag();

  const {
    globalDefault: globalPricingRule,
    pricingRulesObs,
    pricingRulesByCounterpartyBySymbol,
    isPricingRulesLoading,
    isGlobalDefaultLoading,
  } = usePricingRules();
  const customers = useCustomers();
  const customersByName = useCustomersByName();
  const getEntityDisplayName = useCallback(
    (entity: PricingRule) => getPricingRuleEntityDisplayName(customersByName, entity),
    [customersByName]
  );
  const { aggregationsList } = useAggregationsContext();
  const customerAggregations = useMemo(
    () => aggregationsList.filter(agg => agg.AggregationType === AggregationType.Customer),
    [aggregationsList]
  );

  const defaultPricingRuleDialog = useDisclosure();
  const defaultPricingRuleAction = useMemo(
    () => ({
      label: 'Default Pricing Rule',
      onClick: () => defaultPricingRuleDialog.open(),
      variant: ButtonVariants.Priority,
      startIcon: IconName.Pencil,
    }),
    [defaultPricingRuleDialog]
  );
  const handleEditPricingRule = useEditPricingRule({ onSettled: undefined });

  const symbolFilter = useSymbolsFilter();
  const modeFilter = useModeFilter();
  const { allSubAccountBooks } = useSubAccounts();
  const { hedgeSubAccountFilter, tradeSubAccountFilter } = useHedgeTradeSubAccountFilters(allSubAccountBooks);
  const marketFeeModeFilter = useMarketFeeModesFilter();
  const { currenciesList } = useCurrenciesContext();
  const filteredCurrencies = useMemo(() => currenciesList.map(currency => currency.Symbol), [currenciesList]);
  const feeCurrencyFilter = useFeeCurrenciesFilter(filteredCurrencies);
  const pricingAggregationFilter = usePricingAggregationsFilter();
  const requiredCustomerFilter = useRequiredCustomerFilterProperty(requireCustomerPricingRulesCounterpartyFilter);

  const availableFilterableProperties = useMemo(() => {
    const properties = [
      requiredCustomerFilter,
      symbolFilter,
      modeFilter,
      hedgeSubAccountFilter,
      tradeSubAccountFilter,
      marketFeeModeFilter,
      feeCurrencyFilter,
      pricingAggregationFilter,
    ];

    // As per the Entity Admin API, undefined is used to indicate a loading state
    return properties.every(prop => !isNil(prop)) ? properties : undefined;
  }, [
    requiredCustomerFilter,
    symbolFilter,
    modeFilter,
    hedgeSubAccountFilter,
    tradeSubAccountFilter,
    marketFeeModeFilter,
    feeCurrencyFilter,
    pricingAggregationFilter,
  ]);
  const pricingRuleSpaceParameterFilter = useSpaceParameterControlledFilter(
    requireCustomerPricingRulesCounterpartyFilter
      ? CUSTOMER_PRICING_RULES_SPACE_PARAMETER_SUPPORT
      : CUSTOMER_PRICING_RULES_MULTI_SPACE_PARAMETER_SUPPORT,
    availableFilterableProperties
  );
  const {
    requireCustomerPricingRulesCounterpartyFilter: requireLocalCustomerPricingRulesCounterpartyFilter,
    onFilterChanged: syncCounterpartyFilterToProvider,
    hasRequiredCounterpartySelection,
    blotterTableOverlay,
  } = useRequiredCustomerFilter({
    controlledFilter: pricingRuleSpaceParameterFilter.controlledFilter,
    controlledCustomerFilterActive: pricingRuleSpaceParameterFilter.controlledFilterKeys.length > 0,
    requireCustomerPricingRulesCounterpartyFilter,
  });

  const customerSecurities = useCustomerSecurities();
  const { searchableSecurities } = useSecuritiesContext();

  const drawerOptions = useMemo(() => {
    if (!customers || !customerSecurities) {
      return EMPTY_ARRAY;
    }
    return getCustomerPricingTableDrawerOptions({
      pricingRulesByCounterpartyBySymbol,
      customers,
      customerAggregations,
      feeModes: DEALER_FEE_MODE_OPTIONS,
      currencies: DEFAULT_CUSTOMER_CREDITS_CURRENCIES,
      subAccounts: allSubAccountBooks,
      customerSecurities,
      searchableSecurities,
      allowedCrossMarkets: pricingRuleAllowedCrossMarkets,
      allowedCrossCurrencies: pricingRuleAllowedCrossCurrencies,
    });
  }, [
    customers,
    customerSecurities,
    searchableSecurities,
    pricingRulesByCounterpartyBySymbol,
    customerAggregations,
    allSubAccountBooks,
    pricingRuleAllowedCrossMarkets,
    pricingRuleAllowedCrossCurrencies,
  ]);

  return (
    <>
      <EditDefaultPricingRuleDialog {...defaultPricingRuleDialog} handleEditPricingRule={handleEditPricingRule} />
      <EntityAdmin<PricingRule>
        title="Pricing Rules"
        subtitle="View and manage customer pricing rules."
        entityName="Pricing Rule"
        path="/pricing-rules"
        dataObservable={pricingRulesObs}
        entityIDField="Counterparty"
        childIDField="Symbol"
        getEntityDisplayName={getEntityDisplayName}
        globalEntity={globalPricingRule}
        columns={CUSTOMER_PRICING_TABLE_COLUMNS}
        allowAddEntity={isAuthorized(ACTION.EDIT_DEALER)}
        allowBulkEdit={isAuthorized(ACTION.EDIT_DEALER)}
        allowDeleteEntity={isAuthorized(ACTION.EDIT_DEALER)}
        allowEditEntity={isAuthorized(ACTION.EDIT_DEALER)}
        allowModeSwitch={{ isDisabled: () => !isAuthorized(ACTION.EDIT_DEALER) }}
        persistKey={CUSTOMER_PRICING_PERSIST_KEY}
        groupColumnDef={CUSTOMER_PRICING_GROUP_COLUMN_DEF}
        filterableProperties={pricingRuleSpaceParameterFilter.filterableProperties}
        externallyControlledFilter={pricingRuleSpaceParameterFilter.externallyControlledFilter}
        isDataLoading={isPricingRulesLoading || isGlobalDefaultLoading}
        onFilterChanged={syncCounterpartyFilterToProvider}
        blotterTableHidden={!hasRequiredCounterpartySelection}
        blotterTableOverlay={blotterTableOverlay}
        initialFiltersOpen={requireLocalCustomerPricingRulesCounterpartyFilter}
        initialGroupsExpanded={requireLocalCustomerPricingRulesCounterpartyFilter}
        bulkEditFields={[
          'Mode',
          'BidSpread',
          'OfferSpread',
          'AcceptPriceLeniency',
          'PricingAggregation',
          'AllowedCrossCurrencies',
          'AllowedCrossMarkets',
          'TradeSubAccount',
          'HedgeSubAccount',
        ]}
        getDeletePath={entity => {
          if (entity.Symbol != null) {
            return `/pricing-rules/counterparty/${encodeURIComponent(
              entity.Counterparty
            )}/security/${encodeURIComponent(entity.Symbol)}`;
          }
          return `/pricing-rules/counterparty/${encodeURIComponent(entity.Counterparty)}`;
        }}
        getPatchPath={() => '/pricing-rules'}
        // Ignore Tiers responses, and also ignore the default pricing rule response
        userFilterFunc={data => data.Tier == null && data.Counterparty != null}
        panelActions={[defaultPricingRuleAction]}
        drawerOptions={drawerOptions}
        serverDataToRecordMappingFunc={mapCustomerPricingServerDataToRecord}
        getEntityForPatchPost={entity =>
          getCustomerPricingEntityForPatchPost(entity, { globalPricingRule, pricingRulesByCounterpartyBySymbol })
        }
        childEntityName="Symbol Override"
        removeNullValuesFromPatchPayload={true}
      />
    </>
  );
}
